import React, { useState, useEffect, useCallback } from 'react';
import './DetalheCliente.css';
import { apiFetch, ApiError } from './api';
import { getAccessToken } from './auth';
import { mensagemParaCodigo } from './mensagensErro';
import EstadoCarregando from './EstadoCarregando';
import EstadoErro from './EstadoErro';
import EstadoVazio from './EstadoVazio';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const TIPOS_CONTATO = [
  { valor: 'REUNIAO', label: 'Reunião' },
  { valor: 'EMAIL', label: 'E-mail' },
  { valor: 'TELEFONE', label: 'Telefonema' },
  { valor: 'WHATSAPP', label: 'WhatsApp' },
  { valor: 'OUTRO', label: 'Outro' },
];

const TIPOS_ARQUIVO = [
  { valor: 'CONTRATO', label: 'Contrato' },
  { valor: 'DOCUMENTO', label: 'Documento' },
  { valor: 'PROPOSTA', label: 'Proposta' },
  { valor: 'PROCURACAO', label: 'Procuração' },
  { valor: 'OUTRO', label: 'Outro' },
];

const STATUS_MARCA = [
  'Em análise', 'Publicada', 'Registrada', 'Indeferida', 'Em recurso', 'Arquivada',
];

const ETAPA_LABEL = {
  PROSPECTO: 'Prospecto', LEAD: 'Lead', QUALIFICADO: 'Qualificado',
  PROPOSTA: 'Proposta', NEGOCIACAO: 'Negociação', CONTRATO: 'Contrato',
  CLIENTE: 'Cliente', PERDIDO: 'Perdido',
};

function tipoContatoLabel(tipo) {
  return TIPOS_CONTATO.find(t => t.valor === tipo)?.label || tipo || '—';
}

function tipoArquivoLabel(tipo) {
  return TIPOS_ARQUIVO.find(t => t.valor === tipo)?.label || tipo || '—';
}

function formatarData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

function formatarDataHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
}

function formatarMoeda(valor) {
  if (valor == null) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
}

function formatarBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function uploadArquivoApi(clienteId, file, tipo, nome, observacoes) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('tipo', tipo);
  if (nome) fd.append('nome', nome);
  if (observacoes) fd.append('observacoes', observacoes);

  const token = getAccessToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(`${API_BASE_URL}/api/clientes/${clienteId}/arquivos`, {
    method: 'POST',
    headers,
    body: fd,
  });
  if (!resp.ok) {
    const corpo = await resp.json().catch(() => ({}));
    const err = new Error(corpo?.error?.code || `HTTP ${resp.status}`);
    err.code = corpo?.error?.code || null;
    err.status = resp.status;
    throw err;
  }
  return resp.json();
}

const FORM_CONTATO_VAZIO = {
  data: '',
  tipo: 'REUNIAO',
  assunto: '',
  descricao: '',
};

const FORM_ARQUIVO_VAZIO = {
  tipo: 'DOCUMENTO',
  nome: '',
  observacoes: '',
  arquivo: null,
};

const FORM_MARCA_VAZIO = {
  nome: '',
  processo: '',
  classe: '',
  status: 'Em análise',
  dataDeposito: '',
  observacoes: '',
};

function DetalheCliente({ clienteId, onNavegar }) {
  const [cliente, setCliente] = useState(null);
  const [carregandoCliente, setCarregandoCliente] = useState(true);
  const [erroCliente, setErroCliente] = useState(null);

  const [contatos, setContatos] = useState([]);
  const [carregandoContatos, setCarregandoContatos] = useState(true);
  const [erroContatos, setErroContatos] = useState(null);

  const [arquivos, setArquivos] = useState([]);
  const [carregandoArquivos, setCarregandoArquivos] = useState(true);
  const [erroArquivos, setErroArquivos] = useState(null);

  const [marcas, setMarcas] = useState([]);
  const [carregandoMarcas, setCarregandoMarcas] = useState(true);
  const [erroMarcas, setErroMarcas] = useState(null);

  const [modalContatoAberto, setModalContatoAberto] = useState(false);
  const [contatoEditando, setContatoEditando] = useState(null);
  const [formContato, setFormContato] = useState(FORM_CONTATO_VAZIO);
  const [enviandoContato, setEnviandoContato] = useState(false);
  const [erroContato, setErroContato] = useState(null);

  const [modalArquivoAberto, setModalArquivoAberto] = useState(false);
  const [formArquivo, setFormArquivo] = useState(FORM_ARQUIVO_VAZIO);
  const [enviandoArquivo, setEnviandoArquivo] = useState(false);
  const [erroArquivo, setErroArquivo] = useState(null);

  const [modalMarcaAberto, setModalMarcaAberto] = useState(false);
  const [marcaEditando, setMarcaEditando] = useState(null);
  const [formMarca, setFormMarca] = useState(FORM_MARCA_VAZIO);
  const [enviandoMarca, setEnviandoMarca] = useState(false);
  const [erroMarca, setErroMarca] = useState(null);

  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [marcaHistorico, setMarcaHistorico] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState(null);

  const [sincronizandoId, setSincronizandoId] = useState(null);
  const [modalSyncAberto, setModalSyncAberto] = useState(false);
  const [syncMudancas, setSyncMudancas] = useState([]);
  const [erroSync, setErroSync] = useState(null);

  const carregarCliente = useCallback(async () => {
    setCarregandoCliente(true);
    setErroCliente(null);
    try {
      const det = await apiFetch(`/api/clientes/${clienteId}`);
      setCliente(det);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') return;
      setErroCliente(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setCarregandoCliente(false);
    }
  }, [clienteId]);

  const carregarContatos = useCallback(async () => {
    setCarregandoContatos(true);
    setErroContatos(null);
    try {
      const res = await apiFetch(`/api/clientes/${clienteId}/contatos`);
      setContatos(res.contatos ?? []);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') return;
      setErroContatos(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setCarregandoContatos(false);
    }
  }, [clienteId]);

  const carregarArquivos = useCallback(async () => {
    setCarregandoArquivos(true);
    setErroArquivos(null);
    try {
      const res = await apiFetch(`/api/clientes/${clienteId}/arquivos`);
      setArquivos(res.arquivos ?? []);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') return;
      setErroArquivos(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setCarregandoArquivos(false);
    }
  }, [clienteId]);

  const carregarMarcas = useCallback(async () => {
    setCarregandoMarcas(true);
    setErroMarcas(null);
    try {
      const res = await apiFetch(`/api/clientes/${clienteId}/marcas`);
      setMarcas(res.marcas ?? []);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') return;
      setErroMarcas(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setCarregandoMarcas(false);
    }
  }, [clienteId]);

  useEffect(() => {
    carregarCliente();
    carregarContatos();
    carregarArquivos();
    carregarMarcas();
  }, [carregarCliente, carregarContatos, carregarArquivos, carregarMarcas]);

  // Contatos
  const abrirModalNovoContato = () => {
    setContatoEditando(null);
    setFormContato({ ...FORM_CONTATO_VAZIO, data: new Date().toISOString().split('T')[0] });
    setErroContato(null);
    setModalContatoAberto(true);
  };

  const abrirModalEditarContato = (contato) => {
    setContatoEditando(contato);
    setFormContato({
      data: contato.data || '',
      tipo: contato.tipo || 'REUNIAO',
      assunto: contato.assunto || '',
      descricao: contato.descricao || '',
    });
    setErroContato(null);
    setModalContatoAberto(true);
  };

  const fecharModalContato = () => {
    setModalContatoAberto(false);
    setErroContato(null);
  };

  const salvarContato = async () => {
    if (!formContato.data || !formContato.assunto.trim()) {
      setErroContato('Data e assunto são obrigatórios.');
      return;
    }
    setEnviandoContato(true);
    setErroContato(null);
    const corpo = {
      data: formContato.data,
      tipo: formContato.tipo,
      assunto: formContato.assunto.trim(),
      descricao: formContato.descricao.trim() || null,
    };
    try {
      if (contatoEditando) {
        await apiFetch(`/api/clientes/${clienteId}/contatos/${contatoEditando.id}`, { method: 'PUT', body: corpo });
      } else {
        await apiFetch(`/api/clientes/${clienteId}/contatos`, { method: 'POST', body: corpo });
      }
      fecharModalContato();
      carregarContatos();
    } catch (e) {
      setErroContato(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setEnviandoContato(false);
    }
  };

  const excluirContato = async (contatoId) => {
    if (!window.confirm('Excluir este contato?')) return;
    try {
      await apiFetch(`/api/clientes/${clienteId}/contatos/${contatoId}`, { method: 'DELETE' });
      carregarContatos();
    } catch (e) {
      alert(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    }
  };

  // Arquivos
  const abrirModalArquivo = () => {
    setFormArquivo(FORM_ARQUIVO_VAZIO);
    setErroArquivo(null);
    setModalArquivoAberto(true);
  };

  const fecharModalArquivo = () => {
    setModalArquivoAberto(false);
    setErroArquivo(null);
  };

  const handleArquivoFileChange = (e) => {
    const file = e.target.files[0];
    setFormArquivo(prev => ({
      ...prev,
      arquivo: file,
      nome: prev.nome || (file?.name || ''),
    }));
  };

  const salvarArquivo = async () => {
    if (!formArquivo.arquivo) {
      setErroArquivo('Selecione um arquivo.');
      return;
    }
    setEnviandoArquivo(true);
    setErroArquivo(null);
    try {
      await uploadArquivoApi(
        clienteId,
        formArquivo.arquivo,
        formArquivo.tipo,
        formArquivo.nome.trim() || null,
        formArquivo.observacoes.trim() || null,
      );
      fecharModalArquivo();
      carregarArquivos();
    } catch (e) {
      setErroArquivo(mensagemParaCodigo(e?.code || null));
    } finally {
      setEnviandoArquivo(false);
    }
  };

  const excluirArquivo = async (arquivoId) => {
    if (!window.confirm('Excluir este arquivo?')) return;
    try {
      await apiFetch(`/api/clientes/${clienteId}/arquivos/${arquivoId}`, { method: 'DELETE' });
      carregarArquivos();
    } catch (e) {
      alert(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    }
  };

  // Marcas
  const abrirModalNovaMarca = () => {
    setMarcaEditando(null);
    setFormMarca(FORM_MARCA_VAZIO);
    setErroMarca(null);
    setModalMarcaAberto(true);
  };

  const abrirModalEditarMarca = (marca) => {
    setMarcaEditando(marca);
    setFormMarca({
      nome: marca.nome || '',
      processo: marca.processo || '',
      classe: marca.classe || '',
      status: marca.status || 'Em análise',
      dataDeposito: marca.dataDeposito || '',
      observacoes: marca.observacoes || '',
    });
    setErroMarca(null);
    setModalMarcaAberto(true);
  };

  const fecharModalMarca = () => {
    setModalMarcaAberto(false);
    setErroMarca(null);
  };

  const salvarMarca = async () => {
    const nome = formMarca.nome.trim();
    const processo = formMarca.processo.trim();
    if (!nome) { setErroMarca('O nome da marca é obrigatório.'); return; }
    if (nome.length > 200) { setErroMarca('O nome deve ter no máximo 200 caracteres.'); return; }
    if (processo && !/^\d{9,10}$/.test(processo)) {
      setErroMarca('O processo deve ter 9 ou 10 dígitos.'); return;
    }
    const classe = formMarca.classe.trim();
    if (!classe) { setErroMarca('A classe é obrigatória.'); return; }
    if (!/^\d{1,2}$/.test(classe) || Number(classe) < 1 || Number(classe) > 45) {
      setErroMarca('A classe Nice deve ser um número de 1 a 45.'); return;
    }
    if (!formMarca.status) { setErroMarca('O status é obrigatório.'); return; }
    if (formMarca.observacoes.length > 2000) {
      setErroMarca('As observações devem ter no máximo 2000 caracteres.'); return;
    }
    setEnviandoMarca(true);
    setErroMarca(null);
    const corpo = {
      nome,
      processo: processo || null,
      classe: classe.padStart(2, '0'),
      status: formMarca.status,
      dataDeposito: formMarca.dataDeposito || null,
      observacoes: formMarca.observacoes.trim() || null,
    };
    try {
      if (marcaEditando) {
        await apiFetch(`/api/clientes/${clienteId}/marcas/${marcaEditando.id}`, { method: 'PUT', body: corpo });
      } else {
        await apiFetch(`/api/clientes/${clienteId}/marcas`, { method: 'POST', body: corpo });
      }
      fecharModalMarca();
      carregarMarcas();
      carregarCliente();
    } catch (e) {
      setErroMarca(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setEnviandoMarca(false);
    }
  };

  const excluirMarca = async (marcaId) => {
    if (!window.confirm('Excluir esta marca?')) return;
    try {
      await apiFetch(`/api/clientes/${clienteId}/marcas/${marcaId}`, { method: 'DELETE' });
      carregarMarcas();
      carregarCliente();
    } catch (e) {
      alert(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    }
  };

  const abrirHistorico = async (marca) => {
    setMarcaHistorico(marca);
    setModalHistoricoAberto(true);
    setHistorico([]);
    setErroHistorico(null);
    setCarregandoHistorico(true);
    try {
      const res = await apiFetch(`/api/clientes/${clienteId}/marcas/${marca.id}/historico`);
      setHistorico(res.historico ?? []);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') return;
      setErroHistorico(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const fecharHistorico = () => {
    setModalHistoricoAberto(false);
    setMarcaHistorico(null);
  };

  const sincronizarInpi = async (marca) => {
    setSincronizandoId(marca.id);
    setErroSync(null);
    try {
      const res = await apiFetch(
        `/api/clientes/${clienteId}/marcas/${marca.id}/sincronizar-inpi`,
        { method: 'POST' },
      );
      setSyncMudancas(res?.mudancas ?? []);
      setModalSyncAberto(true);
      carregarMarcas();
      carregarCliente();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'SESSION_EXPIRED') return;
      setErroSync(mensagemParaCodigo(e instanceof ApiError ? e.code : null));
      setModalSyncAberto(true);
    } finally {
      setSincronizandoId(null);
    }
  };

  const fecharSync = () => {
    setModalSyncAberto(false);
    setSyncMudancas([]);
    setErroSync(null);
  };

  if (carregandoCliente) {
    return (
      <div className="detalhe-cliente-container">
        <EstadoCarregando mensagem="Carregando dados do cliente..." />
      </div>
    );
  }

  if (erroCliente) {
    return (
      <div className="detalhe-cliente-container">
        <EstadoErro mensagem={erroCliente} onRetry={carregarCliente} />
        <button onClick={() => onNavegar('clientes')} className="btn btn-secondary">
          ← Voltar para lista
        </button>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="detalhe-cliente-container">
        <EstadoErro mensagem="Cliente não encontrado." />
        <button onClick={() => onNavegar('clientes')} className="btn btn-secondary">
          ← Voltar para lista
        </button>
      </div>
    );
  }

  const resumo = cliente.resumo || {};
  const end = cliente.endereco || {};
  const enderecoTexto = [end.logradouro, end.numero, end.complemento, end.bairro, end.cidade, end.uf, end.cep]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="detalhe-cliente-container">
      <div className="detalhe-cliente-header">
        <h1>{cliente.nome}</h1>
        <button onClick={() => onNavegar('clientes')} className="btn btn-secondary">
          ← Voltar para lista
        </button>
      </div>

      {/* Resumo em cards */}
      <div className="resumo-cliente">
        <div className="resumo-item">
          <span className="resumo-valor">{resumo.totalMarcas ?? 0}</span>
          <span className="resumo-rotulo">Marcas</span>
        </div>
        <div className="resumo-item">
          <span className="resumo-valor">{resumo.totalContatos ?? 0}</span>
          <span className="resumo-rotulo">Contatos</span>
        </div>
        <div className="resumo-item">
          <span className="resumo-valor">{resumo.totalArquivos ?? 0}</span>
          <span className="resumo-rotulo">Arquivos</span>
        </div>
        <div className="resumo-item">
          <span className="resumo-valor">{resumo.totalAgendamentosPendentes ?? 0}</span>
          <span className="resumo-rotulo">Agendamentos</span>
        </div>
        {resumo.valorEmAberto != null && (
          <div className="resumo-item">
            <span className="resumo-valor">{formatarMoeda(resumo.valorEmAberto)}</span>
            <span className="resumo-rotulo">Em aberto</span>
          </div>
        )}
        {resumo.valorPago != null && (
          <div className="resumo-item">
            <span className="resumo-valor">{formatarMoeda(resumo.valorPago)}</span>
            <span className="resumo-rotulo">Pago</span>
          </div>
        )}
      </div>

      {/* Dados do cliente */}
      <div className="detalhes-cliente">
        <div className="detalhes-coluna">
          <h3>Dados</h3>
          <div className="detalhe-item">
            <div className="detalhe-label">Tipo</div>
            <div className="detalhe-valor">
              <span className={`tipo-cliente ${cliente.tipo?.toLowerCase()}`}>
                {cliente.tipo === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
              </span>
            </div>
          </div>
          <div className="detalhe-item">
            <div className="detalhe-label">{cliente.tipo === 'PF' ? 'CPF' : 'CNPJ'}</div>
            <div className="detalhe-valor">{cliente.documento || '—'}</div>
          </div>
          <div className="detalhe-item">
            <div className="detalhe-label">Etapa do Funil</div>
            <div className="detalhe-valor">
              <span className={`status-cliente ${cliente.etapaFunil?.toLowerCase()}`}>
                {ETAPA_LABEL[cliente.etapaFunil] || cliente.etapaFunil || '—'}
              </span>
            </div>
          </div>
          <div className="detalhe-item">
            <div className="detalhe-label">Cadastro</div>
            <div className="detalhe-valor">{formatarData(cliente.criadoEm)}</div>
          </div>
          {cliente.observacoes && (
            <div className="detalhe-item">
              <div className="detalhe-label">Observações</div>
              <div className="detalhe-valor">{cliente.observacoes}</div>
            </div>
          )}
        </div>

        <div className="detalhes-coluna">
          <h3>Contato</h3>
          <div className="detalhe-item">
            <div className="detalhe-label">E-mail</div>
            <div className="detalhe-valor">{cliente.email || '—'}</div>
          </div>
          <div className="detalhe-item">
            <div className="detalhe-label">Telefone</div>
            <div className="detalhe-valor">{cliente.telefone || '—'}</div>
          </div>
          {enderecoTexto && (
            <div className="detalhe-item">
              <div className="detalhe-label">Endereço</div>
              <div className="detalhe-valor">{enderecoTexto}</div>
            </div>
          )}
        </div>
      </div>

      {/* Marcas do cliente */}
      <div className="marcas-cliente">
        <h3>
          Marcas
          <button className="adicionar-marca" onClick={abrirModalNovaMarca}>
            + Nova Marca
          </button>
        </h3>

        {carregandoMarcas ? (
          <EstadoCarregando linhas={3} inline />
        ) : erroMarcas ? (
          <EstadoErro mensagem={erroMarcas} onRetry={carregarMarcas} />
        ) : marcas.length === 0 ? (
          <EstadoVazio
            titulo="Sem marcas"
            descricao="Nenhuma marca cadastrada para este cliente."
            acao={{ rotulo: '+ Nova Marca', onClick: abrirModalNovaMarca }}
          />
        ) : (
          <div className="tabela-container">
            <table className="tabela-marcas-cliente">
              <thead>
                <tr>
                  <th>Marca</th>
                  <th>Processo</th>
                  <th>Classe</th>
                  <th>Status</th>
                  <th>Depósito</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {marcas.map(marca => (
                  <tr key={marca.id}>
                    <td><strong>{marca.nome}</strong></td>
                    <td>{marca.processo || '—'}</td>
                    <td>{marca.classe || '—'}</td>
                    <td>
                      <span className={`status-marca ${marca.status?.toLowerCase().replace(/ /g, '-')}`}>
                        {marca.status || '—'}
                      </span>
                    </td>
                    <td>{formatarData(marca.dataDeposito)}</td>
                    <td>
                      <div className="marca-acoes">
                        <button className="botao-acao historico" onClick={() => abrirHistorico(marca)}>
                          Histórico
                        </button>
                        <button
                          className="botao-acao sincronizar"
                          onClick={() => sincronizarInpi(marca)}
                          disabled={sincronizandoId === marca.id || !marca.processo}
                          title={!marca.processo ? 'Cadastre o processo para sincronizar com o INPI' : undefined}
                        >
                          {sincronizandoId === marca.id ? 'Sincronizando...' : 'Sincronizar INPI'}
                        </button>
                        <button className="botao-acao editar" onClick={() => abrirModalEditarMarca(marca)}>
                          Editar
                        </button>
                        <button className="botao-acao excluir" onClick={() => excluirMarca(marca.id)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Histórico de Contatos */}
      <div className="contatos-cliente">
        <h3>
          Histórico de Contatos
          <button className="adicionar-contato" onClick={abrirModalNovoContato}>
            + Registrar Contato
          </button>
        </h3>

        {carregandoContatos ? (
          <EstadoCarregando linhas={3} inline />
        ) : erroContatos ? (
          <EstadoErro mensagem={erroContatos} onRetry={carregarContatos} />
        ) : contatos.length === 0 ? (
          <EstadoVazio
            titulo="Sem contatos"
            descricao="Nenhum contato registrado com este cliente."
            acao={{ rotulo: '+ Registrar Contato', onClick: abrirModalNovoContato }}
          />
        ) : (
          <div className="lista-contatos">
            {contatos.map(contato => (
              <div className="cartao-contato" key={contato.id}>
                <div className="contato-cabecalho">
                  <div className="contato-data">{formatarData(contato.data)}</div>
                  <div className={`contato-tipo ${contato.tipo?.toLowerCase()}`}>
                    {tipoContatoLabel(contato.tipo)}
                  </div>
                </div>
                <div className="contato-assunto">{contato.assunto}</div>
                {contato.descricao && (
                  <div className="contato-descricao">{contato.descricao}</div>
                )}
                {contato.responsavel && (
                  <div className="contato-responsavel">
                    <strong>Responsável:</strong> {contato.responsavel}
                  </div>
                )}
                <div className="contato-acoes">
                  <button className="botao-acao editar" onClick={() => abrirModalEditarContato(contato)}>
                    Editar
                  </button>
                  <button className="botao-acao excluir" onClick={() => excluirContato(contato.id)}>
                    Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Arquivos */}
      <div className="arquivos-cliente">
        <h3>
          Arquivos
          <button className="adicionar-arquivo" onClick={abrirModalArquivo}>
            + Adicionar Arquivo
          </button>
        </h3>

        {carregandoArquivos ? (
          <EstadoCarregando linhas={2} inline />
        ) : erroArquivos ? (
          <EstadoErro mensagem={erroArquivos} onRetry={carregarArquivos} />
        ) : arquivos.length === 0 ? (
          <EstadoVazio
            titulo="Sem arquivos"
            descricao="Nenhum arquivo anexado a este cliente."
            acao={{ rotulo: '+ Adicionar Arquivo', onClick: abrirModalArquivo }}
          />
        ) : (
          <div className="lista-arquivos">
            {arquivos.map(arquivo => (
              <div className="cartao-arquivo" key={arquivo.id}>
                <div className="arquivo-icone">📄</div>
                <div className="arquivo-info">
                  <div className="arquivo-nome">{arquivo.nome}</div>
                  <div className="arquivo-detalhes">
                    <span>{formatarBytes(arquivo.tamanhoBytes)}</span>
                    <span className="arquivo-tipo">{tipoArquivoLabel(arquivo.tipo)}</span>
                    <span>{formatarData(arquivo.dataUpload)}</span>
                    {arquivo.uploadPorNome && <span>por {arquivo.uploadPorNome}</span>}
                  </div>
                  {arquivo.observacoes && (
                    <div className="arquivo-observacoes">{arquivo.observacoes}</div>
                  )}
                </div>
                <div className="arquivo-acoes">
                  {arquivo.urlDownload && (
                    <a
                      href={arquivo.urlDownload}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="botao-acao download"
                    >
                      ⬇ Baixar
                    </a>
                  )}
                  <button className="botao-acao excluir" onClick={() => excluirArquivo(arquivo.id)}>
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Histórico da Marca */}
      {modalHistoricoAberto && marcaHistorico && (
        <div className="modal-overlay" onClick={fecharHistorico}>
          <div className="modal-conteudo" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>Histórico — {marcaHistorico.nome}</h2>
              <button className="modal-fechar" onClick={fecharHistorico}>×</button>
            </div>
            <div className="modal-corpo">
              {carregandoHistorico ? (
                <EstadoCarregando linhas={3} inline />
              ) : erroHistorico ? (
                <EstadoErro mensagem={erroHistorico} onRetry={() => abrirHistorico(marcaHistorico)} />
              ) : historico.length === 0 ? (
                <EstadoVazio
                  titulo="Sem histórico"
                  descricao="Nenhuma alteração registrada para esta marca."
                />
              ) : (
                <ul className="historico-marca">
                  {historico.map(item => (
                    <li key={item.id} className="historico-item">
                      <div className="historico-cabecalho">
                        <span className="historico-data">{formatarDataHora(item.data)}</span>
                        {item.origem && <span className="historico-origem">{item.origem}</span>}
                      </div>
                      <div className="historico-acao">{item.acao}</div>
                      {item.detalhes && <div className="historico-detalhes">{item.detalhes}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-rodape">
              <button className="botao-cancelar" onClick={fecharHistorico}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Resultado da Sincronização INPI */}
      {modalSyncAberto && (
        <div className="modal-overlay" onClick={fecharSync}>
          <div className="modal-conteudo" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>Sincronização INPI</h2>
              <button className="modal-fechar" onClick={fecharSync}>×</button>
            </div>
            <div className="modal-corpo">
              {erroSync ? (
                <div className="erro-modal">{erroSync}</div>
              ) : syncMudancas.length === 0 ? (
                <p className="sync-sem-mudancas">Nenhuma mudança encontrada na base do INPI.</p>
              ) : (
                <table className="tabela-mudancas">
                  <thead>
                    <tr><th>Campo</th><th>Antes</th><th>Depois</th></tr>
                  </thead>
                  <tbody>
                    {syncMudancas.map((m, i) => (
                      <tr key={`${m.campo}-${i}`}>
                        <td>{m.campo}</td>
                        <td>{m.antes || '—'}</td>
                        <td>{m.depois || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-rodape">
              <button className="botao-salvar" onClick={fecharSync}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Marca */}
      {modalMarcaAberto && (
        <div className="modal-overlay" onClick={fecharModalMarca}>
          <div className="modal-conteudo" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>{marcaEditando ? 'Editar Marca' : 'Nova Marca'}</h2>
              <button className="modal-fechar" onClick={fecharModalMarca}>×</button>
            </div>
            <div className="modal-corpo">
              {erroMarca && <div className="erro-modal">{erroMarca}</div>}
              <div className="form-marca">
                <div className="form-grupo" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="marca-nome">Nome *</label>
                  <input
                    id="marca-nome"
                    type="text"
                    maxLength={200}
                    value={formMarca.nome}
                    onChange={e => setFormMarca(p => ({ ...p, nome: e.target.value }))}
                  />
                </div>
                <div className="form-grupo">
                  <label htmlFor="marca-processo">Processo</label>
                  <input
                    id="marca-processo"
                    type="text"
                    placeholder="9 ou 10 dígitos"
                    value={formMarca.processo}
                    onChange={e => setFormMarca(p => ({ ...p, processo: e.target.value }))}
                  />
                </div>
                <div className="form-grupo">
                  <label htmlFor="marca-classe">Classe *</label>
                  <input
                    id="marca-classe"
                    type="text"
                    value={formMarca.classe}
                    onChange={e => setFormMarca(p => ({ ...p, classe: e.target.value }))}
                  />
                </div>
                <div className="form-grupo">
                  <label htmlFor="marca-status">Status *</label>
                  <select
                    id="marca-status"
                    value={formMarca.status}
                    onChange={e => setFormMarca(p => ({ ...p, status: e.target.value }))}
                  >
                    {STATUS_MARCA.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-grupo">
                  <label htmlFor="marca-deposito">Data de Depósito</label>
                  <input
                    id="marca-deposito"
                    type="date"
                    value={formMarca.dataDeposito}
                    onChange={e => setFormMarca(p => ({ ...p, dataDeposito: e.target.value }))}
                  />
                </div>
                <div className="form-grupo" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="marca-obs">Observações</label>
                  <textarea
                    id="marca-obs"
                    rows="2"
                    maxLength={2000}
                    value={formMarca.observacoes}
                    onChange={e => setFormMarca(p => ({ ...p, observacoes: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-rodape">
              <button className="botao-cancelar" onClick={fecharModalMarca} disabled={enviandoMarca}>
                Cancelar
              </button>
              <button className="botao-salvar" onClick={salvarMarca} disabled={enviandoMarca}>
                {enviandoMarca ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Contato */}
      {modalContatoAberto && (
        <div className="modal-overlay" onClick={fecharModalContato}>
          <div className="modal-conteudo" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>{contatoEditando ? 'Editar Contato' : 'Registrar Contato'}</h2>
              <button className="modal-fechar" onClick={fecharModalContato}>×</button>
            </div>
            <div className="modal-corpo">
              {erroContato && <div className="erro-modal">{erroContato}</div>}
              <div className="form-contato">
                <div className="form-grupo">
                  <label htmlFor="contato-data">Data do Contato *</label>
                  <input
                    id="contato-data"
                    type="date"
                    value={formContato.data}
                    onChange={e => setFormContato(p => ({ ...p, data: e.target.value }))}
                  />
                </div>
                <div className="form-grupo">
                  <label htmlFor="contato-tipo">Tipo</label>
                  <select
                    id="contato-tipo"
                    value={formContato.tipo}
                    onChange={e => setFormContato(p => ({ ...p, tipo: e.target.value }))}
                  >
                    {TIPOS_CONTATO.map(t => (
                      <option key={t.valor} value={t.valor}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-grupo" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="contato-assunto">Assunto *</label>
                  <input
                    id="contato-assunto"
                    type="text"
                    value={formContato.assunto}
                    onChange={e => setFormContato(p => ({ ...p, assunto: e.target.value }))}
                  />
                </div>
                <div className="form-grupo" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="contato-descricao">Descrição</label>
                  <textarea
                    id="contato-descricao"
                    rows="3"
                    value={formContato.descricao}
                    onChange={e => setFormContato(p => ({ ...p, descricao: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-rodape">
              <button className="botao-cancelar" onClick={fecharModalContato} disabled={enviandoContato}>
                Cancelar
              </button>
              <button className="botao-salvar" onClick={salvarContato} disabled={enviandoContato}>
                {enviandoContato ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Arquivo */}
      {modalArquivoAberto && (
        <div className="modal-overlay" onClick={fecharModalArquivo}>
          <div className="modal-conteudo" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>Adicionar Arquivo</h2>
              <button className="modal-fechar" onClick={fecharModalArquivo}>×</button>
            </div>
            <div className="modal-corpo">
              {erroArquivo && <div className="erro-modal">{erroArquivo}</div>}
              <div className="form-arquivo">
                <div className="form-grupo" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="arquivo-file">Arquivo *</label>
                  <input id="arquivo-file" type="file" onChange={handleArquivoFileChange} />
                </div>
                <div className="form-grupo">
                  <label htmlFor="arquivo-tipo">Tipo</label>
                  <select
                    id="arquivo-tipo"
                    value={formArquivo.tipo}
                    onChange={e => setFormArquivo(p => ({ ...p, tipo: e.target.value }))}
                  >
                    {TIPOS_ARQUIVO.map(t => (
                      <option key={t.valor} value={t.valor}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-grupo">
                  <label htmlFor="arquivo-nome">Nome (opcional)</label>
                  <input
                    id="arquivo-nome"
                    type="text"
                    placeholder="Nome exibido"
                    value={formArquivo.nome}
                    onChange={e => setFormArquivo(p => ({ ...p, nome: e.target.value }))}
                  />
                </div>
                <div className="form-grupo" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="arquivo-obs">Observações</label>
                  <textarea
                    id="arquivo-obs"
                    rows="2"
                    value={formArquivo.observacoes}
                    onChange={e => setFormArquivo(p => ({ ...p, observacoes: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-rodape">
              <button className="botao-cancelar" onClick={fecharModalArquivo} disabled={enviandoArquivo}>
                Cancelar
              </button>
              <button className="botao-salvar" onClick={salvarArquivo} disabled={enviandoArquivo}>
                {enviandoArquivo ? 'Enviando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DetalheCliente;
